// Hot-form / injury modifiers (Standard mode).
// On each spin, a handful of players get a small boost or knock to ONE stat,
// weighted toward the stats that matter most for their position group.

const STAT_WEIGHTS = {
  DEF: { gk: 0.1, def: 1.0, mid: 0.4, att: 0.15 },
  PAS: { gk: 0.1, def: 0.5, mid: 1.0, att: 0.6 },
  SHO: { gk: 0.0, def: 0.15, mid: 0.5, att: 1.0 },
  DRI: { gk: 0.0, def: 0.3, mid: 0.8, att: 0.9 },
  PAC: { gk: 0.1, def: 0.5, mid: 0.6, att: 0.8 },
  PHY: { gk: 0.3, def: 1.0, mid: 0.7, att: 0.6 },
};
const ATTRS = ["PAC", "SHO", "PAS", "DRI", "DEF", "PHY"];

const AFFECTED_COUNT = 8; // players touched per spin
const MIN_DELTA = 2;
const MAX_DELTA = 4;

const DEF_POS = ["CB", "LB", "RB", "LWB", "RWB"];
const MID_POS = ["CDM", "CM", "CAM", "LM", "RM"];

export function positionGroup(pos) {
  if (pos === "GK") return "gk";
  if (DEF_POS.includes(pos)) return "def";
  if (MID_POS.includes(pos)) return "mid";
  return "att";
}

const clamp = (v) => Math.max(1, Math.min(99, v));

function weightedStat(group) {
  const weights = ATTRS.map((a) => STAT_WEIGHTS[a][group] || 0);
  const total = weights.reduce((s, w) => s + w, 0);
  if (total <= 0) return ATTRS[Math.floor(Math.random() * ATTRS.length)];
  let r = Math.random() * total;
  for (let i = 0; i < ATTRS.length; i++) {
    r -= weights[i];
    if (r <= 0) return ATTRS[i];
  }
  return ATTRS[ATTRS.length - 1];
}

// Returns a NEW team (originals untouched) with form/injury on N random players.
export function applyFormMods(team) {
  if (!team?.players) return team;
  const players = team.players;

  // shuffle indices, take the first N
  const idx = [...players.keys()].filter((i) => players[i].pos !== "GK");
  for (let i = idx.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [idx[i], idx[j]] = [idx[j], idx[i]];
  }
  const chosen = new Set(
    idx.slice(0, Math.min(AFFECTED_COUNT, players.length)),
  );

  const newPlayers = players.map((p, i) => {
    if (!chosen.has(i)) return { ...p, formMod: null };
    const stat = weightedStat(positionGroup(p.pos));
    const isForm = Math.random() < 0.5;
    const mag =
      MIN_DELTA + Math.floor(Math.random() * (MAX_DELTA - MIN_DELTA + 1));
    const base = p.attrs?.[stat] ?? 0;
    const next = clamp(base + (isForm ? mag : -mag));
    const delta = next - base; // after clamping
    if (delta === 0) return { ...p, formMod: null };
    return {
      ...p,
      attrs: { ...p.attrs, [stat]: next },
      formMod: { type: isForm ? "form" : "injury", stat, delta, base },
    };
  });

  return { ...team, players: newPlayers };
}

// Would picking this player create/upgrade a nationality chemistry bonus?
// count === 2 → makes 3 (NATION_3); count === 4 → makes 5 (NATION_5).
export function chemAvailableFor(player, roster) {
  if (!player?.nation) return false;
  let count = 0;
  for (const p of Object.values(roster)) {
    if (p.nation === player.nation) count++;
  }
  return count === 2 || count === 4;
}
